/**
 * GA4 계측 유틸 (FO analytics SSoT) — 마케팅 유입 분석 전용.
 *
 * "어느 채널에서 들어와 어떤 페이지를 보고 전환했는가"를 답하기 위한 계측이다. 제품 사용성 분석은
 * 범위 밖이다.
 *
 * ## 로더를 index.html 이 아니라 여기서 주입하는 이유
 * 1. dev 미로딩 — index.html 엔 env 분기 문법이 없어 `pnpm dev` 에서도 히트가 찍힌다.
 * 2. SSG 오염 — 빌드가 `vite-ssg` 라 index.html 을 템플릿으로 라우트별 HTML 을 프리렌더한다.
 *    거기 gtag 스크립트와 측정 ID 가 그대로 박힌다.
 * 따라서 `initAnalytics()` 를 main.ts 의 isClient 블록에서만 호출해 브라우저에서 동적 주입한다.
 *
 * ## 실패해도 조용히 — 계측이 서비스를 깨뜨리지 않는다
 * 모든 발화는 3중 가드 + try/catch 로 감싸 예외를 밖으로 내지 않는다.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** GA4 측정 ID. 운영에서만 주입되며, 비어 있으면 계측 전체가 no-op 이 된다. */
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';

/** 로더 중복 주입 차단(HMR·중복 호출 대비). */
let loaderInjected = false;

/**
 * GA4 이벤트 1건 발화 — 모든 track* 이 경유하는 단일 통로.
 *
 * 3중 가드를 순서대로 통과해야 실제 전송된다.
 *  1. window 부재 — SSG renderToString 패스(Node). 반드시 첫 번째여야 ReferenceError 를 피한다.
 *  2. 측정 ID 부재 — dev.
 *  3. gtag 부재 — 광고 차단기·네트워크 실패.
 */
function track(eventName: string, params: Record<string, unknown> = {}): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  try {
    if (!window.gtag) return;
    window.gtag('event', eventName, params);
  } catch {
    // 계측 실패는 서비스 흐름에 영향을 주지 않는다 — 조용히 무시.
  }
}

/**
 * GA4 로더 주입 + config. main.ts 의 isClient 블록에서 1회 호출한다.
 * 측정 ID 미설정(dev)·SSR·중복 호출은 무해한 no-op.
 *
 * `send_page_view: false` 가 핵심 — 끄지 않으면 로더 자동 발화와 라우터 afterEach 발화가 겹쳐
 * 랜딩 페이지뷰가 항상 2배로 잡힌다. 페이지뷰는 전량 trackPageView 로만 보낸다.
 */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (loaderInjected) return;
  try {
    loaderInjected = true;

    window.dataLayer = window.dataLayer ?? [];
    // gtag 는 반드시 arguments 를 그대로 push 하는 함수여야 한다(배열 스프레드로 바꾸면 GA4 가 인식 못 함).
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    document.head.append(script);
  } catch {
    // 주입 실패 시에도 track() 이 gtag 부재로 no-op 이 되므로 별도 복구를 하지 않는다.
  }
}

/**
 * SPA 가상 페이지뷰 1건.
 *
 * @param screen 논리 화면명(route.name, kebab-case) — `/guide/:slug` 처럼 파라미터로 갈라지는 경로를
 *   하나로 묶어 집계하기 위한 축. 빈 값이면 'unknown'.
 * @param path 전체 경로(fullPath) — utm_*·gclid 등 유입 파라미터를 보존해야 하므로 쿼리를 포함한다.
 */
export function trackPageView(screen: string, path: string): void {
  track('page_view', {
    screen_name: screen || 'unknown',
    page_path: path,
    page_title: typeof document === 'undefined' ? '' : document.title,
  });
}

/** 회원가입 완료. */
export function trackSignUp(method: 'local'): void {
  track('sign_up', { method });
}

// ── 성적 분석 비로그인 미리보기(077) — 샘플 뷰 전용 커스텀 이벤트 3종 ─────────
//
// 로그인 여부 구분은 별도 파라미터가 아니라 **이 이벤트들의 존재**로 성립한다 — 샘플 뷰에서만
// 발화하고 회원 뷰(실데이터)에서는 절대 발화하지 않는다(FR-018). 파라미터는 축·행동명만이며
// 성적값·대학명·개인정보를 싣지 않는다(analytics-contract.md).

/** 샘플 뷰 축 — GA4 `axis` 파라미터 값. */
export type SampleAxis = 'jeongsi' | 'susi' | 'tests';

/** 샘플 뷰 조회(커스텀 이벤트) — `useSamplePreview` 가 마운트 시 1회 보낸다. */
export function trackSamplePreviewView(axis: SampleAxis): void {
  track('sample_preview_view', { axis });
}

/** 상단 배너 [로그인하고 내 성적으로 분석하기] 클릭(커스텀 이벤트). */
export function trackSampleLoginCta(axis: SampleAxis): void {
  track('sample_login_cta', { axis });
}

/**
 * 잠금 행동 시도(커스텀 이벤트) — 실데이터 경계 행동에서 로그인 안내 모달이 뜨기 직전.
 * @param action `LockAction` 값 그대로(snake 변환 없음 — 예 `filter`, `start:CAREER`).
 */
export function trackSampleLockAttempt(axis: SampleAxis, action: string): void {
  track('sample_lock_attempt', { axis, action });
}
